import React, { useState, useEffect } from 'react';
import { trendsAPI } from '../utils/api';
import Copyright from '../components/Copyright';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';

const PERIODS = [
  { key: 'week', label: '7 Days' },
  { key: 'month', label: '30 Days' },
  { key: 'year', label: '1 Year' },
];

const METRICS = [
  { key: 'weight',       label: 'Weight',       unit: 'lbs',   color: '#4f8ef7', field: 'weight',              type: 'line' },
  { key: 'bmi',          label: 'BMI',           unit: '',      color: '#a855f7', field: 'bmi',                  type: 'line' },
  { key: 'blood_sugar',  label: 'Blood Sugar',   unit: 'mg/dL', color: '#f43f5e', field: 'fasting_blood_sugar',  type: 'line' },
  { key: 'calories',     label: 'Calories',      unit: 'kcal',  color: '#f59e0b', field: 'caloric_intake',       type: 'bar'  },
  { key: 'water',        label: 'Water',         unit: 'oz',    color: '#06b6d4', field: 'water_intake',         type: 'bar'  },
  { key: 'protein',      label: 'Protein',       unit: 'g',     color: '#4f8ef7', field: 'protein_g',            type: 'bar'  },
  { key: 'carbs',        label: 'Carbs',         unit: 'g',     color: '#f59e0b', field: 'carbs_g',              type: 'bar'  },
  { key: 'fat',          label: 'Fat',           unit: 'g',     color: '#e94560', field: 'fat_g',                type: 'bar'  },
  { key: 'fiber',        label: 'Fiber',         unit: 'g',     color: '#22c55e', field: 'fiber_g',              type: 'bar'  },
  { key: 'sugar',        label: 'Sugar',         unit: 'g',     color: '#fb923c', field: 'sugar_g',              type: 'bar'  },
  { key: 'sodium',       label: 'Sodium',        unit: 'mg',    color: '#94a3b8', field: 'sodium_mg',            type: 'bar'  },
  { key: 'sleep_hours',  label: 'Sleep Hours',   unit: 'hrs',   color: '#818cf8', field: 'hours_sleep',          type: 'bar'  },
  { key: 'sleep_rating', label: 'Sleep Rating',  unit: '/5',    color: '#c084fc', field: 'rating_sleep',         type: 'line' },
  { key: 'day_rating',   label: 'Day Rating',    unit: '/5',    color: '#34d399', field: 'avg_day_rating',       type: 'line' },
];

const METRIC_GROUPS = [
  { label: 'Body',      keys: ['weight', 'bmi', 'blood_sugar'] },
  { label: 'Nutrition', keys: ['calories', 'water', 'protein', 'carbs', 'fat', 'fiber', 'sugar', 'sodium'] },
  { label: 'Wellness',  keys: ['sleep_hours', 'sleep_rating', 'day_rating'] },
];

function calcStats(points, field) {
  const clean = points.reduce((acc, p) => {
    const v = parseFloat(p[field]);
    if (p[field] != null && p[field] !== '' && !isNaN(v)) acc.push(v);
    return acc;
  }, []);
  if (clean.length === 0) return null;
  const avg = clean.reduce((a, b) => a + b, 0) / clean.length;
  return { avg, min: Math.min(...clean), max: Math.max(...clean), count: clean.length };
}

const CustomTooltip = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}>
      <p style={{ color: 'var(--text-2)', marginBottom: 4, fontSize: 11 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill, fontWeight: 600 }}>
          {parseFloat(p.value).toFixed(1)} {unit}
        </p>
      ))}
    </div>
  );
};

function StatBox({ label, value, unit, color, highlight }) {
  return (
    <div style={{
      flex: 1, textAlign: 'center', padding: '10px 6px',
      background: highlight ? `${color}18` : 'var(--bg-3)',
      borderRadius: 8,
      border: highlight ? `1px solid ${color}35` : '1px solid var(--border)',
    }}>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: highlight ? color : 'var(--text)', letterSpacing: '0.02em' }}>
        {value != null ? parseFloat(value).toFixed(1) : '—'}
      </div>
      {unit && <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 1 }}>{unit}</div>}
      <div style={{ fontSize: 11, color: 'var(--text-2)', fontWeight: 500 }}>{label}</div>
    </div>
  );
}

function MetricCard({ metric, points }) {
  const stats = calcStats(points, metric.field);
  if (!stats) return null;

  const chartData = points
    .filter(p => p[metric.field] != null && !isNaN(parseFloat(p[metric.field])) && p[metric.field] !== '')
    .map(p => ({ date: (p.log_date || '').split('T')[0].slice(5), value: parseFloat(p[metric.field]) }));

  const change = chartData.length >= 2 ? chartData[chartData.length - 1].value - chartData[0].value : null;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 12, height: 12, borderRadius: 3, background: metric.color }} />
          <span style={{ fontWeight: 600, fontSize: 15 }}>{metric.label}</span>
          {metric.unit && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>({metric.unit})</span>}
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{stats.count} entries</span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <StatBox label="Average" value={stats.avg} unit={metric.unit} color={metric.color} highlight />
        <StatBox label="Low" value={stats.min} unit={metric.unit} color={metric.color} />
        <StatBox label="High" value={stats.max} unit={metric.unit} color={metric.color} />
        {change != null && (
          <StatBox
            label="Change"
            value={change}
            unit={metric.unit}
            color={change > 0 ? '#e94560' : change < 0 ? '#22c55e' : '#94a3b8'}
          />
        )}
      </div>

      {chartData.length >= 2 ? (
        <ResponsiveContainer width="100%" height={160}>
          {metric.type === 'bar' ? (
            <BarChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: 'var(--text-3)', fontSize: 10 }} tickLine={false} />
              <YAxis tick={{ fill: 'var(--text-3)', fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip unit={metric.unit} />} />
              <ReferenceLine y={stats.avg} stroke={metric.color} strokeDasharray="4 4" strokeOpacity={0.5} />
              <Bar dataKey="value" fill={metric.color} radius={[3, 3, 0, 0]} opacity={0.85} />
            </BarChart>
          ) : (
            <LineChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: 'var(--text-3)', fontSize: 10 }} tickLine={false} />
              <YAxis tick={{ fill: 'var(--text-3)', fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip unit={metric.unit} />} />
              <ReferenceLine y={stats.avg} stroke={metric.color} strokeDasharray="4 4" strokeOpacity={0.5} />
              <Line type="monotone" dataKey="value" stroke={metric.color} strokeWidth={2.5}
                dot={{ fill: metric.color, r: 3, strokeWidth: 0 }}
                activeDot={{ r: 5 }} connectNulls />
            </LineChart>
          )}
        </ResponsiveContainer>
      ) : (
        <div style={{ textAlign: 'center', padding: '12px 0', color: 'var(--text-3)', fontSize: 13 }}>
          Only 1 data point — log more to see a trend line
        </div>
      )}
    </div>
  );
}

export default function TrendsPage() {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(() =>
    Object.fromEntries(METRICS.map(m => [m.key, ['weight', 'bmi', 'calories', 'protein', 'carbs', 'fat', 'sleep_hours', 'day_rating'].includes(m.key)]))
  );

  useEffect(() => {
    setLoading(true);
    trendsAPI.getTrends(period).then(res => setData(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, [period]);

  const toggleMetric = key => setEnabled(p => ({ ...p, [key]: !p[key] }));
  const toggleGroup = keys => {
    const allOn = keys.every(k => enabled[k]);
    setEnabled(p => { const n = { ...p }; keys.forEach(k => { n[k] = !allOn; }); return n; });
  };

  const points = data?.data_points || [];
  const summary = data?.summary || {};
  const enabledMetrics = METRICS.filter(m => enabled[m.key]);

  const summaryCards = [
    { label: 'Avg Weight', value: summary.avg_weight, unit: 'lbs', color: '#4f8ef7' },
    { label: 'Avg BMI', value: summary.avg_bmi, unit: '', color: '#a855f7' },
    { label: 'Avg Calories', value: summary.avg_calories ? Math.round(summary.avg_calories) : null, unit: 'kcal', color: '#f59e0b' },
    { label: 'Weight Δ', value: summary.weight_change, unit: 'lbs', color: parseFloat(summary.weight_change) > 0 ? '#e94560' : '#22c55e' },
    { label: 'Avg Protein', value: summary.avg_protein, unit: 'g', color: '#4f8ef7' },
    { label: 'Avg Sleep', value: summary.avg_sleep_hours, unit: 'hrs', color: '#818cf8' },
    { label: 'Day Rating', value: summary.avg_day_rating, unit: '/5', color: '#34d399' },
    { label: 'Total Logs', value: summary.total_logs, unit: '', color: 'var(--text)' },
  ].filter(s => s.value != null && s.value !== '' && !isNaN(parseFloat(s.value)));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">TRENDS</h1>
          <p style={{ color: 'var(--text-2)', fontSize: 14 }}>
            {points.length} data points · {period === 'week' ? '7 days' : period === 'month' ? '30 days' : '1 year'}
          </p>
        </div>
        <div className="tabs">
          {PERIODS.map(p => (
            <button key={p.key} className={`tab ${period === p.key ? 'active' : ''}`} onClick={() => setPeriod(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="page-body">
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : points.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📈</div>
            <div className="empty-state-title">No data yet</div>
            <div className="empty-state-sub">Start logging to see your trends</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 20, alignItems: 'start' }}>

            {/* Metric Selector */}
            <div style={{ position: 'sticky', top: 20 }}>
              <div className="card" style={{ padding: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>
                  Show Metrics
                </div>

                {METRIC_GROUPS.map(group => {
                  const available = group.keys.filter(k => {
                    const m = METRICS.find(x => x.key === k);
                    return m && calcStats(points, m.field);
                  });
                  if (available.length === 0) return null;
                  const allOn = available.every(k => enabled[k]);

                  return (
                    <div key={group.label} style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          {group.label}
                        </span>
                        <button onClick={() => toggleGroup(available)}
                          style={{ fontSize: 11, background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                          {allOn ? 'None' : 'All'}
                        </button>
                      </div>
                      {available.map(key => {
                        const m = METRICS.find(x => x.key === key);
                        const stats = calcStats(points, m.field);
                        if (!stats) return null;
                        return (
                          <label key={key} style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '6px 8px', borderRadius: 7, cursor: 'pointer', marginBottom: 2,
                            background: enabled[key] ? `${m.color}12` : 'transparent',
                            transition: 'background 0.15s',
                          }}>
                            <input type="checkbox" checked={!!enabled[key]} onChange={() => toggleMetric(key)}
                              style={{ accentColor: m.color, width: 14, height: 14, flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 500, color: enabled[key] ? 'var(--text)' : 'var(--text-3)' }}>
                                {m.label}
                              </div>
                              <div style={{ fontSize: 10, color: 'var(--text-3)' }}>
                                avg {parseFloat(stats.avg).toFixed(1)}{m.unit}
                              </div>
                            </div>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                              background: enabled[key] ? m.color : 'var(--border)' }} />
                          </label>
                        );
                      })}
                    </div>
                  );
                })}

                <div className="divider" />
                <div style={{ fontSize: 11, color: 'var(--text-3)', textAlign: 'center' }}>
                  {enabledMetrics.length} / {METRICS.length} metrics
                </div>
              </div>
            </div>

            {/* Charts */}
            <div>
              {/* Summary row */}
              {summaryCards.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 10, marginBottom: 20 }}>
                  {summaryCards.map(s => (
                    <div key={s.label} className="card card-sm" style={{ textAlign: 'center' }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: s.color, letterSpacing: '0.02em' }}>
                        {typeof s.value === 'number' ? s.value.toFixed ? s.value.toFixed(1) : s.value : s.value}
                        {s.unit && <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 2 }}>{s.unit}</span>}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {enabledMetrics.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">📊</div>
                  <div className="empty-state-title">No metrics selected</div>
                  <div className="empty-state-sub">Check some metrics on the left</div>
                </div>
              ) : (
                enabledMetrics.map(metric => (
                  <MetricCard key={metric.key} metric={metric} points={points} />
                ))
              )}

              {data?.exercise_summary?.length > 0 && (
                <div className="card">
                  <div className="section-title">💪 Exercise Breakdown</div>
                  <table className="table">
                    <thead><tr><th>Exercise</th><th>Sessions</th><th>Avg Weight</th><th>Total Time</th></tr></thead>
                    <tbody>
                      {data.exercise_summary.map((ex, i) => (
                        <tr key={i}>
                          <td style={{ fontWeight: 500 }}>{ex.exercise_name}</td>
                          <td>{ex.sessions}</td>
                          <td>{ex.avg_weight ? `${parseFloat(ex.avg_weight).toFixed(1)}lbs` : '—'}</td>
                          <td>{ex.total_minutes ? `${Math.round(ex.total_minutes)}m` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <Copyright />
    </div>
  );
}
